<template>
    <div>
        <button @click="baseModalOpen = true" data-id="wizard-modal-button" class="btn btn-primary pull-right">
            Create Channel
        </button>
        <product-alerts :status="alert" v-model="alert"></product-alerts>
        <transition name="fade">
            <div v-if="baseModalOpen" class="modal-wrapper">
                <div class="modal-bg" @click="baseModalOpen = false"></div>
                <div class="d-flex flex-column modal-inner p-4 w-100" style="max-width: 42rem;">
                    <div v-if="shopifyModalStep !== 0" class="row w-100 d-flex align-items-center px-4 pb-0 mx-auto ">
                        <div class="progress-bar__wrapper col-md-12 mx-auto mt-2 flex align-items-center px-0">
                            <div class="progress-bar__bg w-100"></div>
                            <div class="progress-bar__buttons d-flex w-100 align-items-center justify-content-between">
                                <button v-for="index in 6"
                                    :key="index"
                                    class="progress-bar__button"
                                    :class="{'progress-active': shopifyModalStep === index, 'progress-complete': shopifyModalStep > index, 'cursor-pointer': stepsCompleted > index }"
                                    @click="shopifyModalStep = index"
                                    :disabled="shopifyModalStep === index || stepsCompleted < index"
                                >
                                    {{ index }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <transition name="fade">
                        <div class="row mx-4 pb-1 mt-3 d-flex flex-column" v-if="shopifyModalStep === 0">
                            <div class="row my-0 py-0 w-100 mx-auto d-flex flex-column">
                                <div class="col-md-12">
                                    <h4 class="mt-0 mb-4 wizard-title">
                                        What kind of channel would you like to add?
                                    </h4>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto d-flex flex-column">
                                <div class="col-md-12">
                                    <label class="font-weight-bold mt-2">
                                        Select Channel Type
                                    </label>
                                    <select data-id="wizard-channel-type" class="form-control" v-model="selectedChannelType">
                                        <option :value="channel" v-for="channel in channelTypes" :key="channel">
                                            {{ channel }}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-md-12">
                                    <label class="font-weight-bold mt-2">
                                        Name
                                    </label>
                                    <input data-id="wizard-channel-name" type="text" class="form-control" v-model="channelName">
                                </div>
                            </div>
                        </div>
                        <div class="row mx-4 pb-1 mt-3 d-flex flex-column" v-else-if="shopifyModalStep === 1">
                            <div class="row my-0 py-0 w-100 mx-auto d-flex flex-column">
                                <div class="col-md-12">
                                    <h4 class="mt-0 mb-4 wizard-title">
                                        What are your Shopify API credentials?
                                    </h4>
                                    <p class="text-muted mb-0 max-w-para">
                                        Shopify API Key should correspond to <strong>API Key</strong> field.
                                        <a href="/images/shopify_api_key.png" target="_blank"><i class="fa fa-eye"></i></a>
                                        <br />
                                        Shopify Access Token should be entered in the <strong>Password</strong> field.
                                        <a href="/images/shopify_api_password.png" target="_blank"><i class="fa fa-eye"></i></a>
                                        <br />
                                        For the API URL, copy your Shopify store address and add <em>/admin/api</em> at the end (for example:
                                        <strong>https://_______.myshopify.com/admin/api</strong>).
                                    </p>
                                </div>
                            </div>
                            <div class="row mt-2 mb-0 py-0 w-100 mx-auto">
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        API Key
                                    </label>
                                    <generic-tooltip :message="'You can find the details in your Shopify account under Apps -> APP Name.  It should be a 32 character string.'">
                                        <input v-model="shopifyChannel.apiKey" type="text" class="form-control" slot="tooltip_sibling">
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                </div>
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        API URL
                                    </label>
                                    <generic-tooltip :message="'Ex: https://storename.myshopify.com/admin/api/. You can find the details in your Shopify Account.'">
                                        <input v-model="shopifyChannel.apiURL" type="text" class="form-control" slot="tooltip_sibling">
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto">
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        Password
                                    </label>
                                    <generic-tooltip :message="'You can find the details in your Shopify account under Apps -> APP Name.  It should be a 38 character string beginning with shpss.'">
                                        <input v-model="shopifyChannel.password" type="password" class="form-control" slot="tooltip_sibling">
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                </div>
                            </div>
                        </div>
                        <div class="row mx-4 pb-1 mt-3 d-flex flex-column" v-else-if="shopifyModalStep === 2">
                            <div class="row my-0 py-0 w-100 mx-auto d-flex flex-column">
                                <div class="col-md-12">
                                    <h4 class="mt-0 mb-4 wizard-title">
                                        What is the currency your store is using to sell products on this channel?
                                    </h4>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto">
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        Price Base
                                    </label>
                                    <generic-tooltip :message="'Choose the price records you wish to send to this channel. NOTE: should you wish to sell products without prices you may still be required to select a Price Base'">
                                        <select slot="tooltip_sibling" name="" id="" class="form-control" @change="handleSelectCurrency" :value="shopifyChannel.priceBase" v-if="availableCurrencies.length > 0">
                                            <option v-for="currency in availableCurrencies" :key="currency" :value="currency">
                                                {{ currency }}
                                            </option>
                                        </select>
                                        <input slot="tooltip_sibling" v-model="shopifyChannel.priceBase" type="text" class="form-control" v-if="availableCurrencies.length == 0">
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                </div>
                            </div>
                        </div>
                        <div class="row mx-4 pb-1 mt-3 d-flex flex-column" v-else-if="shopifyModalStep === 3">
                            <div class="row my-0 py-0 w-100 mx-auto d-flex flex-column">
                                <div class="col-md-12">
                                    <h4 class="mt-0 mb-4 wizard-title">
                                        What is the warehouse location your store is using to stock inventory on this channel?
                                    </h4>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto">
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        Inventory Location
                                    </label>
                                    <generic-tooltip :message="'What is the warehouse location your store is using to stock inventory on this channel? NOTE: should you wish not to track inventory, you are still required to choose an Inventory Location for setup'">
                                        <input type="text" class="form-control" v-model="shopifyChannel.inventoryLocation" slot="tooltip_sibling">
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                </div>
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        Inventory Sync
                                    </label>
                                    <generic-tooltip :message="'Do you want the PIM to sync inventory changes to your store?'">
                                        <select name="" id="" class="form-control" v-model="shopifyChannel.inventorySync" slot="tooltip_sibling">
                                            <option default :value="false">
                                                Disabled
                                            </option>
                                            <option :value="true">
                                                Enabled
                                            </option>
                                        </select>
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                    <p class="text-muted text-xxs mb-0" v-if="shopifyChannel.inventorySync">
                                        Inventory changes will be pushed
                                    </p>
                                    <p class="text-muted text-xxs mb-0" v-else>
                                        Inventory changes will be ignored
                                    </p>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto">
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        Inventory Tracking
                                    </label>
                                    <generic-tooltip :message="'Do you want Shopify to track and manage inventory quantity for your store?'">
                                        <select name="" id="" class="form-control" v-model="shopifyChannel.inventoryTracking" slot="tooltip_sibling">
                                            <option default :value="'disabled'">
                                                Disabled
                                            </option>
                                            <option :value="'none'">
                                                None
                                            </option>
                                            <option :value="'enabled'">
                                                Products/Variants
                                            </option>
                                        </select>
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                    <p class="text-muted text-xxs mb-0" v-if="shopifyChannel.inventoryTracking === 'disabled'">
                                        Leave the existing store settings as is
                                    </p>
                                    <p class="text-muted text-xxs mb-0" v-else-if="shopifyChannel.inventoryTracking === 'none'">
                                        Prevent inventory tracking in Shopify
                                    </p>
                                    <p class="text-muted text-xxs mb-0" v-else-if="shopifyChannel.inventoryTracking === 'enabled'">
                                        Allow inventory tracking in Shopify
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="row mx-4 pb-1 mt-3 d-flex flex-column" v-else-if="shopifyModalStep === 4">
                            <div class="row my-0 py-0 w-100 mx-auto d-flex flex-column">
                                <div class="col-md-12">
                                    <h4 class="mt-0 mb-4 wizard-title">
                                        What is the main category you are using inside the PIM to manage your products on this channel?
                                    </h4>
                                    <p class="text-muted mb-0">
                                        Jasper PIM requires at least one 'Root' category in order to tell the PIM channel configurator where to look for products.
                                        <strong>Please note:</strong> once the store is configured the value for the <strong>Base Category</strong> field
                                        cannot be changed. To learn more about categories <a href="https://jasperpim.atlassian.net/wiki/spaces/JPKB/pages/57377576/Understanding+Categories">
                                        <strong>click here</strong></a>.
                                    </p>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto">
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        Base Category
                                    </label>
                                    <generic-tooltip :message="'Select the Root level category used to create the menu structure on the channel. Note: once selected, it is no longer editable.'">
                                        <select slot="tooltip_sibling" name="" id="" class="form-control" @change="handleSelectCategory" :value="shopifyChannel.baseCategory.id">
                                            <option v-for="category in categories" :key="category.id" :value="category.id">
                                                {{ category.name }}
                                            </option>
                                        </select>
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                </div>
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        Base URL
                                    </label>
                                    <generic-tooltip :message="'The URL stem of your storefront (this allows for quick access to the admin and front end for a specific product)'">
                                        <input type="text" class="form-control" v-model="shopifyChannel.baseURL" slot="tooltip_sibling">
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto">
                                <div class="col-md-12 w-100 py-2 mt-4">
                                    <span class="cursor-pointer text-muted font-weight-bold"
                                          v-text="categoryFormOpen ? 'Create Category -' : 'Create Category +'"
                                          @click="categoryFormOpen = !categoryFormOpen">
                                    </span>
                                </div>
                                <div v-if="categoryFormOpen" class="col-md-12 flex flex-column p-4" style="border: solid 1px #dedede;">
                                    <div class="row pb-0">
                                        <div class="d-flex flex-column col-md-6">
                                            <label class="font-weight-bold">
                                                Name
                                            </label>
                                            <input type="text" class="form-control" v-model="categoryForm.name">
                                        </div>
                                    </div>
                                    <div class="row pb-0 mt-0">
                                        <div class="col-md-6">
                                            <button class="btn btn-sm btn-primary mt-2" @click="createNewCategory" :disabled="!categoryForm.name.length || pendingRequest">
                                                Create
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row mx-4 pb-1 mt-3 d-flex flex-column" v-else-if="shopifyModalStep === 5">
                            <div class="row my-0 py-0 w-100 mx-auto d-flex flex-column">
                                <div class="col-md-12">
                                    <h4 class="mt-0 mb-4 wizard-title">
                                        What is the main version you are going to use to publish products on this channel?
                                    </h4>
                                    <p class="text-muted mb-0">
                                        A version in Jasper PIM provides a means of storing distinctive content on a product while maintaining its unique identifying properties such as
                                        Brand and SKU. Versions allow you to sell in multiple languages or specify unique content within the same language. To learn more
                                        <a href="https://jasperpim.atlassian.net/wiki/spaces/JPKB/pages/57770498/Understanding+Versions"><strong>click here</strong></a>.
                                    </p>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto">
                                <div class="col-md-6 w-100">
                                    <label class="font-weight-bold mt-2">
                                        Version
                                    </label>
                                    <generic-tooltip :message="'If versions are configured for your PIM, select the version to publish to the chosen channel. Otherwise choose Master.'">
                                        <select name="" id="" class="form-control" slot="tooltip_sibling" @change="handleSelectVersion" :value="shopifyChannel.version.id">
                                            <option v-for="version in versions" :key="version.id" :value="version.id">{{ version.name }}</option>
                                        </select>
                                        <span slot="tooltip_trigger" class="d-flex align-items-center justify-content-center text-muted">
                                            <i class="fas fa-info-circle"></i>
                                        </span>
                                    </generic-tooltip>
                                </div>
                            </div>
                            <div class="row my-0 py-0 w-100 mx-auto">
                                <div class="col-md-12 w-100 py-2 mt-4">
                                    <span class="cursor-pointer text-muted font-weight-bold"
                                          v-text="versionFormOpen ? 'Create Version -' : 'Create Version +'"
                                          @click="versionFormOpen = !versionFormOpen">
                                    </span>
                                </div>
                                <div v-if="versionFormOpen" class="col-md-12 flex flex-column p-4" style="border: solid 1px #dedede;">
                                    <div class="row">
                                        <div class="d-flex flex-column col-md-6">
                                            <label class="font-weight-bold">
                                                Name
                                            </label>
                                            <input class="form-control" type="text" v-model="versionForm.name">
                                        </div>
                                        <div class="d-flex flex-column col-md-6">
                                            <label class="font-weight-bold">
                                                Slug
                                            </label>
                                            <input class="form-control" type="text" v-model="versionForm.slug">
                                        </div>
                                    </div>
                                    <div class="row pb-0 mt-0">
                                        <div class="col-md-6">
                                            <button class="btn btn-sm btn-primary mt-2" @click="createNewVersion" :disabled="!versionForm.name.length || !versionForm.slug.length || pendingRequest">
                                                Create
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row mx-4 pb-1 mt-3 d-flex flex-column" v-else-if="shopifyModalStep === 6">
                            <div class="row my-0 py-0 w-100 mx-auto d-flex flex-column">
                                <div class="col-md-12">
                                    <h4 class="mt-0 mb-4 wizard-title">
                                        Please confirm the values shown are correct
                                    </h4>
                                </div>
                                <div class="col-md-12 w-100 confirmation-grid text-muted">
                                    <div class="confirmation-item d-flex justify-content-between align-items-center">
                                        <div class="text-xs">
                                            <strong>
                                                API URL
                                            </strong>
                                        </div>
                                        <div class="text-xxs">
                                            {{ this.shopifyChannel.apiURL }}
                                        </div>
                                    </div>
                                    <div class="confirmation-item d-flex justify-content-between align-items-center">
                                        <div class="text-xs">
                                            <strong>
                                                Inventory Sync
                                            </strong>
                                        </div>
                                        <div class="text-xxs">
                                            {{ this.shopifyChannel.inventorySync }}
                                        </div>
                                    </div>
                                    <div class="confirmation-item d-flex justify-content-between align-items-center">
                                        <div class="text-xs">
                                            <strong>
                                                Inventory Location
                                            </strong>
                                        </div>
                                        <div class="text-xxs">
                                            {{ this.shopifyChannel.inventoryLocation }}
                                        </div>
                                    </div>
                                    <div class="confirmation-item d-flex justify-content-between align-items-center">
                                        <div class="text-xs">
                                            <strong>
                                                Price Base
                                            </strong>
                                        </div>
                                        <div class="text-xxs">
                                            {{ this.shopifyChannel.priceBase }}
                                        </div>
                                    </div>
                                    <div class="confirmation-item d-flex justify-content-between align-items-center">
                                        <div class="text-xs">
                                            <strong>
                                                Version
                                            </strong>
                                        </div>
                                        <div class="text-xxs">
                                            {{ this.shopifyChannel.version.name }}
                                        </div>
                                    </div>
                                    <div class="confirmation-item d-flex justify-content-between align-items-center">
                                        <div class="text-xs">
                                            <strong>
                                                Base Category
                                            </strong>
                                        </div>
                                        <div class="text-xxs">
                                            {{ this.shopifyChannel.baseCategory.name }}
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-12">
                                    <div class="d-flex flex-column">
                                        <label class="font-weight-bold mt-4">
                                            Would you like to import products from Shopify?
                                        </label>
                                        <div class="d-flex align-items-center">
                                            <input type="checkbox" style="width: 38px;" class="form-control form-checkbox" v-model="shopifyChannel.import"></input>
                                            <p class="text-muted text-xs ml-2 mb-0" v-text="shopifyChannel.import ? 'Yes' : 'No'"></p>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                    <div class="row mx-4 mt-4 mb-0 pb-0">
                        <div class="row my-0 py-0 w-100 mx-auto">
                            <div class="col-md-8">
                                <button class="btn my-2 btn-outline-secondary" @click="baseModalOpen = false" v-if="shopifyModalStep === 0">
                                    Cancel
                                </button>
                                <button class="btn my-2 btn-outline-secondary" v-else @click="shopifyModalStep -= 1">
                                    Previous
                                </button>
                                <button class="btn my-2 ml-2 btn-primary"
                                    data-id="wizard-next-button"
                                    :disabled="advanceButtonDisabled || pendingRequest"
                                    @click="handleModalRequest"
                                    v-text="shopifyModalStep === 6 || selectedChannelType !== 'Shopify' ? 'Save' : 'Next'"
                                ></button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';
import GenericTooltip from "../generic/generic-tooltip.vue";
import ProductAlerts from '../products/product-details-alerts.vue';

const base_url = window.location.protocol + "//" + window.location.host;

export default {
    name: "onboarding-wizard",
    props: {
        channelTypes: {
            required: true,
            type: [Object, Array, String]
        },
    },
    components:{
        'generic-tooltip': GenericTooltip,
        'product-alerts': ProductAlerts,
    },
    data() {
        return {
            baseModalOpen: false,
            selectedChannelType: 'BigCommerce',
            channelName: '',
            shopifyModalStep: 0,
            stepsCompleted: 0,
            versions: [],
            categories: [],
            availableCurrencies: [],
            pendingRequest: false,
            versionFormOpen: false,
            versionForm: {
                name: '',
                slug: ''
            },
            categoryFormOpen: false,
            categoryForm: {
                name: '',
            },
            alert: {
                status: 'alert-danger',
                message: '',
                timeOut: 20000
            },
            shopifyChannel: {
                apiKey: '',
                apiURL: '',
                password: '',
                inventoryLocation: '',
                priceBase: '',
                import: false,
                version: {
                    id: 0,
                    name: "Master",
                    slug: ""
                },
                baseCategory: {
                    id: 1,
                    name: 'Root',
                    slug: 'root'
                },
                baseURL: '',
                inventorySync: false,
                inventoryTracking: 'disabled',
            }
        }
    },
    mounted() {
        this.alert.message = '';

        axios.get(base_url + '/pimapi/languages')
            .then((response) => {
                let versions = response.data.payload;

                versions.unshift({
                    id: 0,
                    name: "Master",
                    slug: ""
                });

                this.versions = versions;
            })
            .catch((error) => {
                console.log('Error loading versions...');
            });
    },
    methods: {
        createNewCategory() {
            this.pendingRequest = true;

            axios.post(base_url + '/channels/wizard/create_category', {
                name: this.categoryForm.name,
                channel_type: this.channelTypesMap[this.selectedChannelType]
            })
                .then(function(response) {
                    const newCategory = {
                        id: response.data.category_id,
                        name: response.data.category_name
                    }

                    axios.get(base_url + '/pimapi/categories/root')
                        .then(function(response) {
                            this.categories = response.data;

                            this.$nextTick(() => {
                                this.shopifyChannel.baseCategory = Object.assign({}, newCategory);

                                this.pendingRequest = false;
                                this.categoryFormOpen = false;
                                this.categoryForm = {
                                    name: ''
                                };
                            });
                        }.bind(this))
                        .catch(function(err) {
                            console.log('err', err);
                            this.pendingRequest = false;
                            this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                        }.bind(this));
                }.bind(this))
                .catch(function(err) {
                    this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                }.bind(this));
        },
        createNewVersion() {
            this.pendingRequest = true;

            axios.post(base_url + '/channels/wizard/create_version', {
                name: this.versionForm.name,
                slug: this.versionForm.slug,
                channel_type: this.channelTypesMap[this.selectedChannelType]
            })
                .then(function(response) {
                    const newVersion = {
                        id: response.data.language_id,
                        name: response.data.language_name
                    };

                    axios.get(base_url + '/pimapi/languages')
                        .then(function(response) {
                            let versions = response.data.payload;

                            versions.unshift({
                                id: 0,
                                name: "Master",
                                slug: ""
                            });

                            this.versions = versions;

                            this.$nextTick(() => {
                                this.shopifyChannel.version = Object.assign({}, newVersion);

                                this.pendingRequest = false;
                                this.versionFormOpen = false;
                                this.versionForm = {
                                    name: '',
                                    slug: ''
                                };
                            });

                            console.log('success');
                        }.bind(this))
                        .catch(function(err) {
                            this.pendingRequest = false;
                            this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                        }.bind(this));
                }.bind(this))
                .catch(function(err) {
                    this.pendingRequest = false;
                    this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                }.bind(this));
        },
        handleSelectCategory(e) {
            this.shopifyChannel.baseCategory = this.categories.find(category => category.id == e.target.value);
        },
        handleSelectCurrency(e) {
            this.shopifyChannel.priceBase = this.availableCurrencies.find(currency => currency == e.target.value);
        },
        handleSelectVersion(e) {
            this.shopifyChannel.version = this.versions.find(version => version.id == e.target.value);
        },
        advanceModalPosition() {
            this.shopifyModalStep += 1;

            if (this.stepsCompleted < this.shopifyModalStep) {
                this.stepsCompleted = this.shopifyModalStep;
            }
        },
        handleModalRequest() {
            switch(this.shopifyModalStep) {
                case 0:
                    this.submitWizardStepZero();
                    break;
                case 1:
                    this.submitWizardStepOne();
                    break;
                case 2:
                    this.submitWizardStepTwo();
                    break;
                case 3:
                    this.submitWizardStepThree();
                    break;
                case 4:
                    this.submitWizardStepFour();
                    break;
                case 5:
                    this.submitWizardStepFive();
                    break;
                case 6:
                    this.submitShopifyCredentials();
                    break;
                default:
                    this.shopifyModalStep === 0;
            }
        },
        submitWizardStepZero() {
            this.pendingRequest = true;

            axios.post(base_url + '/channels/wizard/step_zero', {
                    channel_name: this.channelName,
                    channel_type: this.channelTypesMap[this.selectedChannelType]
                })
                .then(function(response) {
                    this.pendingRequest = false;

                    if (this.selectedChannelType === 'Shopify') {
                        this.advanceModalPosition();
                    } else {
                        this.channelName = "";
                        this.alert.status = "alert-success";
                        this.alert.message = "The channel has been created.";
                        this.baseModalOpen = false;

                        setTimeout(function() {
                            window.location.reload();
                        }, 1000);
                    }
                }.bind(this))
                .catch(function(err) {
                    this.pendingRequest = false;
                    this.alert.status = "alert-danger";
                    this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                }.bind(this));
        },
        submitWizardStepOne() {
            this.pendingRequest = true;

            axios.post(base_url + '/channels/wizard/step_one', {
                    shopify_api_key: this.shopifyChannel.apiKey,
                    shopify_api_url: this.shopifyChannel.apiURL,
                    shopify_password: this.shopifyChannel.password,
                    name: this.channelName,
                    channel_type: this.channelTypesMap[this.selectedChannelType]
                })
                .then(function(response) {
                    this.pendingRequest = false;
                    this.shopifyChannel.base_url = response.data.base_url;
                    this.shopifyChannel.inventoryLocation = response.data.inventory_location;
                    this.shopifyChannel.priceBase = response.data.price_base;
                    this.availableCurrencies = response.data.available_currencies;
                    this.categories = response.data.categories;

                    this.advanceModalPosition();
                }.bind(this))
                .catch(function(err) {
                    this.pendingRequest = false;
                    this.alert.status = "alert-danger";
                    this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                }.bind(this));
        },
        submitWizardStepTwo() {
            this.pendingRequest = true;

            axios.post(base_url + '/channels/wizard/step_two', {
                    price_base: this.shopifyChannel.priceBase,
                    name: this.channelName,
                    channel_type: this.channelTypesMap[this.selectedChannelType]
                })
                .then(function(response) {
                    this.pendingRequest = false;
                    this.advanceModalPosition();
                }.bind(this))
                .catch(function(err) {
                    this.pendingRequest = false;
                    this.alert.status = "alert-danger";
                    this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                }.bind(this));
        },
        submitWizardStepThree() {
            this.pendingRequest = true;

            axios.post(base_url + '/channels/wizard/step_three', {
                    inventory_location: this.shopifyChannel.inventoryLocation,
                    sync: this.shopifyChannel.inventorySync,
                    inventory_tracking: this.shopifyChannel.inventoryTracking,
                    name: this.channelName,
                    channel_type: this.channelTypesMap[this.selectedChannelType]
                })
                .then(function(response) {
                    this.pendingRequest = false;
                    this.advanceModalPosition();
                }.bind(this))
                .catch(function(err) {
                    console.log(err)
                    this.pendingRequest = false;
                    this.alert.status = "alert-danger";
                    this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                }.bind(this));
        },
        submitWizardStepFour() {
            this.pendingRequest = true;

            let baseCategoryId = this.shopifyChannel.baseCategory.id;

            axios.post(base_url + '/channels/wizard/step_four', {
                    base_url: this.shopifyChannel.baseURL,
                    name: this.channelName,
                    base_category_id: baseCategoryId,
                    channel_type: this.channelTypesMap[this.selectedChannelType],
                })
                .then(function(response) {
                    this.pendingRequest = false;
                    this.advanceModalPosition();
                }.bind(this))
                .catch(function(err) {
                    console.log(err.response.data.message);

                    this.pendingRequest = false;
                    this.alert.status = "alert-danger";
                    this.alert.message = err.response.data.message ? err.response.data.message : 'An Error Occurred';
                }.bind(this));
        },
        submitWizardStepFive() {
            this.pendingRequest = true;

            axios.post(base_url + '/channels/wizard/step_five', {
                    language: this.shopifyChannel.version.name,
                    name: this.channelName,
                    channel_type: this.channelTypesMap[this.selectedChannelType]
                })
                .then(function(response) {
                    this.pendingRequest = false;
                    this.advanceModalPosition();
                }.bind(this))
                .catch(function(err) {
                    this.pendingRequest = false;
                    this.alert.status = "alert-danger";
                    this.alert.message = err.response ? err.response.data.message : 'An Error Occurred';
                }.bind(this));
        },
        resetShopifyChannel() {
            this.shopifyChannel = {
                apiKey: '',
                apiURL: '',
                password: '',
                inventoryLocation: '',
                priceBase: '',
                import: false,
                version: {
                    id: 0,
                    name: "Master",
                    slug: ""
                },
                baseCategory: {
                    id: 1,
                    name: 'Root',
                    slug: 'root'
                },
                baseURL: '',
                inventorySync: false,
                inventoryTracking: 'disabled',
            };
        },
        submitShopifyCredentials() {
            this.pendingRequest = true;

            axios.post(base_url + '/channels/wizard/submit', {
                'channel_name': this.channelName,
                'channel_type': this.channelTypesMap[this.selectedChannelType],
                'price_base': this.shopifyChannel.priceBase,
                'inventory_location': this.shopifyChannel.inventoryLocation,
                'inventorySync': this.shopifyChannel.inventorySync,
                'import': this.shopifyChannel.import,
                'base_url': this.shopifyChannel.base_url,
                'data': {
                    'items': [
                        {
                            groupingName: 'Credentials',
                            groupItems: [
                                {
                                    'name' : 'shopify_password',
                                    'value': this.shopifyChannel.password
                                },
                                {
                                    'name' : 'shopify_api_key',
                                    'value': this.shopifyChannel.apiKey
                                },
                                {
                                    'name' : 'shopify_api_url',
                                    'value': this.shopifyChannel.apiURL
                                }
                            ]
                        },
                        {
                            groupingName: 'Settings',
                            groupItems: [
                                {
                                    'name' : 'send_inventory_quantity',
                                    'value': this.shopifyChannel.inventorySync
                                },
                                {
                                    'name' : 'inventory_location',
                                    'value': this.shopifyChannel.inventoryLocation
                                },
                                {
                                    'name' : 'inventory_tracking',
                                    'value': this.shopifyChannel.inventoryTracking
                                },
                                {
                                    'name' : 'price_base',
                                    'value': this.shopifyChannel.priceBase
                                },
                                {
                                    'name' : 'language',
                                    'value': this.shopifyChannel.version.id
                                },
                                {
                                    'name' : 'base_category_id',
                                    'value': this.shopifyChannel.baseCategory.id
                                },
                                {
                                    'name' : 'base_url',
                                    'value': this.shopifyChannel.base_url
                                }
                            ]
                        }
                    ]
                }
            })
            .then(function(response) {
                this.resetShopifyChannel();

                this.alert.status = "alert-success";
                this.alert.message = "The channel has been created.";

                this.pendingRequest = false;
                this.baseModalOpen = false;

                setTimeout(function() {
                    window.location.reload();
                }, 1000);
            }.bind(this))
            .catch(function(err) {
                console.log(err)
                this.pendingRequest = false;
            }.bind(this));
        }
    },
    watch: {
        'versionForm.name': {
            handler: _.debounce(function() {
                if (!this.versionForm.slug.length) {
                    this.versionForm.slug =
                        this.versionForm.name.toLowerCase()
                                             .replace(/[^\w ]+/g, '')
                                             .replace(/ +/g, '-');
                }
            }, 2000)
        }
    },
    computed: {
        advanceButtonDisabled() {
            if (this.shopifyModalStep === 0 && !this.channelName.length) {
                return true
            }

            if (this.shopifyModalStep === 1 &&
                (!this.shopifyChannel.apiKey.length || !this.shopifyChannel.apiURL.length || !this.shopifyChannel.password.length)) {
                return true
            }

            if (this.shopifyModalStep === 4 && !this.shopifyChannel.baseURL.length) {
                return true
            }

            return false;
        },
        channelTypesMap() {
            if (this.channelTypes) {
                const channelsById = Object.entries(this.channelTypes).map(
                    ([key, value]) => [value, parseInt(key)]
                );

                return Object.fromEntries(channelsById);
            }
            return {};
        },
    }
}
</script>

<style lang="scss" scoped>
.wizard-title {
    max-width: 40ch;
    margin-bottom: 1rem !important;
}

.progress-bar__wrapper {
    height: 1.5rem;

    .progress-bar__bg {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        height: 2px;
        background: #dedede;
        z-index: 1;
    }

    .progress-bar__buttons {
        z-index: 2;
        position: absolute;
    }

    .progress-bar__button {
        width: 1.5rem;
        height: 1.5rem;
        border-radius: 50%;
        background: #dedede;
        color: black;
        border: none;
    }

    .progress-complete {
        background: #00b1d9;
        color: white;
    }

    .progress-active {
        background: #1d4c6f;
        color: white;
    }

    .confirmation-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-row-gap: 0.25rem;
    }
}
</style>
